import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { format, parseISO } from 'date-fns';
import { isEqual } from 'lodash-es';
import { CartesianGrid, Line, LineChart, YAxis } from 'recharts';

import Button from '@asteria/component-core/button';
import Dropdown, { DropdownItem } from '@asteria/component-core/dropdown';
import Icon from '@asteria/component-core/icon';
import { Title } from '@asteria/component-core/typography';
import Wrapper, {
	Content,
	Header as WrapperHeader,
} from '@asteria/component-core/wrapper';

import Chip from '@asteria/component-chip/chip';

import * as AppStore from '@asteria/datalayer/stores/app';
import * as ModalStore from '@asteria/datalayer/stores/modals';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { useTranslationFn } from '@asteria/language/hooks';
import { cn } from '@asteria/utils-funcs/classes';
import formatNumber from '@asteria/utils-funcs/formatNumber';

import Header from '../../components/header';
import { ForecasterContext } from '../../context';

const ScenarioItem = React.memo(function ScenarioItem(props) {
	const { scenario, active, onSubmit } = props;
	const translate = useTranslationFn();
	const dispatch = useDispatch();
	const onSelect = React.useCallback(() => {
		onSubmit?.('scenario:selected', active ? null : scenario);
	}, [onSubmit, scenario, active]);

	const tags = useSelector(AppStore.selectors.tags);

	const { items, breakdown, total } = React.useMemo(() => {
		const data = Object.values(
			scenario.operations
				.filter(({ op }) => op !== 'REMOVE')
				.reduce((acc, operation) => {
					const paymentDate = format(
						parseISO(operation.payload.paymentDate),
						'yyyy-MM',
					);
					const total = operation.payload.total;

					if (!acc[paymentDate]) {
						acc[paymentDate] = {
							paymentDate,
							total: 0,
						};
					}

					acc[paymentDate].total +=
						operation.payload.type === 'WITHDRAW' ? -total : total;

					return acc;
				}, []),
		);

		const { items, total } = data.reduce(
			(acc, item) => {
				acc.items.push(item);

				item.total += acc.total;
				acc.total = item.total;

				return acc;
			},
			{ items: [], total: 0 },
		);

		const breakdown = scenario.operations
			.filter(({ op }) => op !== 'REMOVE')
			.reduce(
				(acc, operation) => {
					const [tag] = operation?.payload?.tags ?? [];

					if (!tag) {
						return acc;
					}

					if (!acc.tags[tag]) {
						acc.tags[tag] = {
							tag: (tags ?? []).find((item) => item._id === tag),
							total: 0,
						};
					}

					acc.tags[tag].total += operation.payload.total;
					acc.total += operation.payload.total ?? 0;

					return acc;
				},
				{ total: 0, tags: {} },
			);

		return {
			items,
			breakdown: { ...breakdown, tags: Object.values(breakdown.tags) },
			total,
		};
	}, [scenario.operations, tags]);

	const onEdit = React.useCallback(() => {
		onSubmit?.('scenario:edit', scenario);
	}, [onSubmit, scenario]);

	const onRemove = React.useCallback(() => {
		dispatch(
			ModalStore.open({
				type: ModalStore.MODAL_WINDOWS.ScenarioRemove,
				data: { data: scenario },
			}),
		);

		if (active) {
			onSubmit?.('scenario:selected', null);
		}
	}, [dispatch, scenario, active, onSubmit]);

	return (
		<Wrapper className="w-1/3">
			<WrapperHeader>
				<Title type="title" size="xl" className="grow">
					{scenario.name}
				</Title>
				{active ? (
					<Chip
						label="Active"
						icon="check"
						iconPosition="first"
						size="small"
						className="asteria-state-completed"
					/>
				) : null}
				<Dropdown
					toggle={{
						as: 'div',
						children: <Icon icon="cog" />,
					}}
				>
					<DropdownItem key="1" onClick={onSelect}>
						{active
							? translate('button.disable')
							: translate('button.enable')}
					</DropdownItem>
					<DropdownItem key="1" onClick={onEdit}>
						{translate('button.edit')}
					</DropdownItem>
					<DropdownItem key="2" onClick={onRemove}>
						{translate('button.remove')}
					</DropdownItem>
				</Dropdown>
			</WrapperHeader>
			<Content>
				<Title size="xs">Projected revenue result</Title>
				<Title
					size="xl"
					className={cn(
						{
							'text-red-500': total < 0,
							'text-green-500': total > 0,
						},
						'mb-4',
					)}
				>
					{translate('forecaster.page.scenarios.total', {
						data: { total },
					})}
				</Title>
				<LineChart width={600} height={200} data={items}>
					<YAxis
						tickFormatter={(value) =>
							formatNumber(value, false, false, false, {
								useReduction: true,
							})
						}
						interval={0}
						includeHidden
					/>
					<CartesianGrid
						stroke="var(--color-graph-yaxis-part-line-normal-background,var(--color-graph-part-line-background,#cfcfcf))"
						strokeWidth={0.5}
						vertical={false}
					/>
					<Line dataKey="total" />
				</LineChart>
				{/*<div className="flex flex-row w-full rounded-md overflow-hidden">
					{breakdown.tags.map((tag) => {
						if (!tag.tag) {
							return null;
						}

						return (
							<div
								key={tag.tag._id}
								className={cn(
									'flex flex-row bg-tags-taxes-tax-background h-4',
									{
										'bg-tags-taxes-tax-background':
											tag.total / breakdown.total > 0.5,
									},
								)}
								style={{
									width: `${
										(tag.total / breakdown.total) * 100
									}%`,
								}}
							></div>
						);
					})}
				</div>*/}
			</Content>
		</Wrapper>
	);
});

const ScenariosPage = (props) => {
	const { className } = props;
	const { onSubmit } = React.useContext(ForecasterContext);
	const [showModal, setShowModal] = React.useState(false);
	const translate = useTranslationFn();
	const dispatch = useDispatch();

	const selectedScenarioId = useSelector(
		(store) => ScenarioStore.selectors.scenarioId(store),
		isEqual,
	);

	const scenarios = useSelector(ScenarioStore.selectors.scenarios).filter(
		(scenario) => scenario.name !== '$base',
	);

	const onCreate = React.useCallback(() => {
		dispatch(
			ModalStore.open({
				type: ModalStore.MODAL_WINDOWS.ScenarioCreate,
				data: { type: 'FORECAST' },
			}),
		);
	}, [dispatch]);

	return (
		<div
			className={cn(
				'asteria-component__forecaster-page',
				'asteria--type-home',
				className,
			)}
		>
			<Header />
			<div className="flex flex-row gap-4 m-8 flex-wrap">
				{scenarios.map((scenario) => (
					<ScenarioItem
						key={scenario._id}
						scenario={scenario}
						active={scenario._id === selectedScenarioId}
						onSubmit={onSubmit}
					/>
				))}
			</div>
			<Button variant="tertiary" icon="plus" onClick={onCreate} />
		</div>
	);
};

export default ScenariosPage;
